import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, Send, ArrowLeft, Sparkles, User, Bot,
  Heart, Dumbbell, Target, Flame, RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const quickPrompts = [
  { icon: Dumbbell, text: "Recommend a workout for me", context: "workout" },
  { icon: Target, text: "Help me set a fitness goal", context: "general" },
  { icon: Flame, text: "I need motivation today", context: "general" },
  { icon: Heart, text: "What's a legacy moment?", context: "legacy" }
];

export default function LegacyCoach() {
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [stats, setStats] = useState(null);

  // Fetch user stats
  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/fitness/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setStats(await res.json());
        }
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    };
    fetchStats();
  }, [user]);

  // Initial greeting
  useEffect(() => {
    if (messages.length === 0) {
      const greeting = user 
        ? `Welcome back! I'm your Legacy Coach.

I'm here to help you build a healthier, stronger you—not just for today, but for the people and years ahead.

${stats?.enrolled ? `I see you've completed **${stats.total_sessions} workouts** with a **${stats.current_streak}-day streak**. That's commitment!` : ''}

**What would you like to work on today?** I can recommend workouts, help you set goals, or just chat about what's on your mind.`
        : `Welcome to LegacyFit! I'm your Legacy Coach.

I believe health is a legacy—not just for yourself, but for your family, community, and future generations.

**Tell me a bit about yourself.** What brings you here today? Are you starting fresh, returning to fitness, or looking to level up?

*Log in to track your progress and get personalized recommendations.*`;

      setMessages([{ role: 'assistant', content: greeting }]);
    }
  }, [messages.length, user, stats]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (messageText, context = 'general') => {
    if (!messageText.trim()) return;
    if (!user) {
      toast.error('Please log in to chat with the Legacy Coach');
      return;
    }

    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setSending(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/fitness/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: messageText,
          context: context,
          session_id: sessionId
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        throw new Error('Failed to get response');
      }
    } catch (err) {
      toast.error('Failed to send message');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Please try again in a moment." 
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleQuickPrompt = (prompt) => {
    sendMessage(prompt.text, prompt.context);
  };

  const resetChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-rose-50/30" data-testid="legacy-coach">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white py-8">
        <div className="container mx-auto px-4">
          <Link to="/fitness" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to LegacyFit
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-xl">
              <Heart className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Legacy Coach</h1>
              <p className="text-purple-100">Your AI wellness companion</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* User Stats Card (if logged in and enrolled) */}
        {user && stats?.enrolled && (
          <Card className="mb-6 bg-gradient-to-br from-rose-50 to-purple-50 border-rose-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-rose-600">{stats.total_sessions}</div>
                    <p className="text-xs text-stone-500">Workouts</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{stats.current_streak}</div>
                    <p className="text-xs text-stone-500">Day Streak</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{stats.legacy_moments}</div>
                    <p className="text-xs text-stone-500">Reflections</p>
                  </div>
                </div>
                <Badge className="bg-rose-100 text-rose-700 capitalize">{stats.current_track} Track</Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Chat Messages */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="h-[400px] overflow-y-auto space-y-4 mb-4">
              <AnimatePresence>
                {messages.map((msg, idx) => (
                  <motion.div
                    key={`item-${idx}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`p-2 rounded-full h-fit ${
                      msg.role === 'user' 
                        ? 'bg-rose-100 text-rose-600' 
                        : 'bg-purple-100 text-purple-600'
                    }`}>
                      {msg.role === 'user' ? <User className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
                    </div>
                    <div className={`flex-1 p-4 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-rose-600 text-white rounded-tr-none'
                        : 'bg-stone-100 text-stone-800 rounded-tl-none'
                    }`}>
                      <div className="whitespace-pre-wrap text-sm leading-relaxed">
                        {msg.content.split('**').map((part, i) => 
                          i % 2 === 1 ? <strong key={`item-${i}`}>{part}</strong> : part
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {sending && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-3"
                >
                  <div className="p-2 rounded-full bg-purple-100 text-purple-600">
                    <Bot className="h-5 w-5" />
                  </div>
                  <div className="bg-stone-100 p-4 rounded-2xl rounded-tl-none">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Prompts */}
            {messages.length <= 1 && (
              <div className="grid grid-cols-2 gap-2 mb-4">
                {quickPrompts.map((prompt, idx) => (
                  <Button
                    key={`item-${idx}`}
                    variant="outline"
                    className="justify-start text-left h-auto py-3 px-4"
                    onClick={() => handleQuickPrompt(prompt)}
                    disabled={sending || !user}
                  >
                    <prompt.icon className="h-4 w-4 mr-2 text-rose-500" />
                    <span className="text-sm">{prompt.text}</span>
                  </Button>
                ))}
              </div>
            )}

            {/* Input */}
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={user ? "Ask your Legacy Coach..." : "Log in to chat..."}
                disabled={sending || !user}
                className="flex-1"
              />
              <Button 
                type="submit" 
                disabled={sending || !input.trim() || !user}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Send className="h-4 w-4" />
              </Button>
              {messages.length > 1 && (
                <Button 
                  type="button"
                  variant="outline"
                  onClick={resetChat}
                  title="Start new conversation"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              )}
            </form>

            {!user && (
              <p className="text-center text-sm text-stone-500 mt-3">
                <Link to="/login" className="text-rose-600 hover:underline">Log in</Link> to chat with your Legacy Coach
              </p>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <p className="font-medium text-purple-800 mb-1">About Your Legacy Coach</p>
                <p className="text-sm text-purple-700">
                  I'm here for everyone—the grandmother who wants to play with her grandchildren, 
                  the worker whose body is tired but spirit is strong, the young person who's 
                  never felt comfortable in a gym. I believe in you.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
